import { getProctoringOptionDisabledDetails, isOptionSupportedForPlatform } from "@verity/proctoring-option";
import {
	Application,
	OptionRelatedInstituteSettings,
	ProctoringOption,
	ProctoringPlatforms,
} from "@verity/proctoring-option/types";
import { VerityInstituteSettings } from "../../types/common";
import {
	GROUPED_OPTION_TEXT,
	isTabKey,
	PROCTORING_OPTION_TEXT,
	ProctoringOptionCategory,
	RenderableProctoringKey,
} from "../proctoring-options/option-text/ProctoringOptionText";
import { Trans } from "react-i18next";
import { NestedOption } from "./ConfigurationSummaryList";
import i18nPackage from "../../i18n/i18n";

export function getUniqueApplicationNamesAndIcons(
	applicationNames: string[],
	applications: Application[],
): Record<string, string | undefined> {
	const matchedApps: Record<string, string | undefined> = {};
	const uniqueApps = new Set<string>();

	applicationNames.forEach((appName) => {
		const matchedApp = applications.find((app) => {
			return app.processName.includes(appName);
		});
		const matchedAppName = matchedApp ? matchedApp.name : appName;
		const matchedAppIcon = matchedApp ? matchedApp.iconSvg : undefined;

		if (!uniqueApps.has(matchedAppName)) {
			uniqueApps.add(matchedAppName);
			matchedApps[matchedAppName] = matchedAppIcon;
		}
	});

	return matchedApps;
}

export function filterOptionsByPlatform(
	option: Partial<ProctoringOption>,
	proctoringPlatforms: (keyof ProctoringPlatforms)[],
): (keyof ProctoringOption)[] {
	const filteredOptions: (keyof ProctoringOption)[] = [];
	const excludedKeys = new Set<keyof ProctoringOption>(["isExtension", "isLockdown", "isMobile"]);

	// Iterate over entries and ensure keys are treated as keyof ProctoringOption
	for (const [key, value] of Object.entries(option) as [
		keyof ProctoringOption,
		(typeof option)[keyof ProctoringOption],
	][]) {
		if (excludedKeys.has(key)) {
			// Skip the excluded keys
			continue;
		}

		if (!proctoringPlatforms.some((platform) => isOptionSupportedForPlatform(platform, key, value))) {
			continue;
		}

		if (typeof value === "boolean" && value) {
			// If the value is boolean and true, add the key to the list
			filteredOptions.push(key);
		} else if (typeof value === "string" && value !== "off") {
			// If the value is a string and not 'off', add the key to the list
			filteredOptions.push(key);
		} else if (Array.isArray(value) && value.length > 0) {
			// If the value is an array and not empty, add the key to the list
			filteredOptions.push(key);
		} else if (key === "flaggedKeywords" && value != null) {
			filteredOptions.push(key);
		}
	}

	return filteredOptions;
}

// These keys are handled by the parent key so skip in categorizeProctoringOptions
const SKIP_SUB_OPTION_KEYS = [
	"secondaryVideoRecordingMode",
	"faceTrackingMode",
	"objectDetectionMode",
	"quietEnvironment",
	"audioTranscription",
	"flaggedKeywords",
	"tabWhiteList",
	"closeOpenTabs",
	"disableNewTab",
	"recordWebTraffic",
	"applicationBlockList",
	"allowedApplicationsList",
	"allowedExtensionsList",
	"keyboardShortcutBlockList",
	"excludedWebTrafficRecordingUrls",
	"idVerificationEnableAi",
	"idVerificationMode",
];

function getActiveProctoringPlatforms(
	option: Partial<ProctoringOption>,
	verityInstituteSettings?: VerityInstituteSettings,
): (keyof ProctoringPlatforms)[] {
	const proctoringPlatformsKey: (keyof ProctoringPlatforms)[] = [];

	if (option.isExtension) {
		if (!verityInstituteSettings || verityInstituteSettings.allowExtensionPlatform) {
			proctoringPlatformsKey.push("isExtension");
		}
	}
	if (option.isLockdown) {
		if (!verityInstituteSettings || verityInstituteSettings.allowLockdownPlatform) {
			proctoringPlatformsKey.push("isLockdown");
		}
	}
	if (option.isMobile) {
		if (!verityInstituteSettings || verityInstituteSettings.allowMobilePlatform) {
			proctoringPlatformsKey.push("isMobile");
		}
	}

	return proctoringPlatformsKey;
}

/**
 * Moved this function a helper so vite doesn't refresh when changing UI
 * each entry in optionChildren has this shape:
 * ProctoringKey -> NestedOption[]
 *
 * ProctoringKey will render header + description + icon taken from ProctoringOptionText
 *
 * Then all the NestedOption[] will be rendered in same block as the Proctoring Key (so underneath the header/desc etc)
 *
 * If NestedOption has just text -> it will render a checkmark (see renderNestedOption)
 *
 * If NestedOption has isList: true, children and the children is an array of strings -> it will render a list.
 * The text on the NestedOption will be used as the list title (see renderList)
 */
export function categorizeProctoringOptions(
	option: Partial<ProctoringOption>,
	verityInstituteSettings?: VerityInstituteSettings,
	optionRelatedInstituteSettings?: OptionRelatedInstituteSettings,
): {
	optionCategories: Record<ProctoringOptionCategory, RenderableProctoringKey[]>;
	optionChildren: Map<RenderableProctoringKey, NestedOption[]>;
} {
	const proctoringPlatformsKey = getActiveProctoringPlatforms(option, verityInstituteSettings);
	const activeOptions = filterOptionsByPlatform(option, proctoringPlatformsKey);

	const optionCategories = activeOptions.reduce(
		(acc, key) => {
			if (isTabKey(key)) {
				const category = GROUPED_OPTION_TEXT["tabControl"]["category"] ?? "otherActivities";
				if (!acc[category]) acc[category] = [];

				if (!acc[category].includes("tabControl")) {
					acc[category].push("tabControl");
				}
				return acc;
			}

			if (SKIP_SUB_OPTION_KEYS.includes(key)) {
				return acc;
			}

			const category = PROCTORING_OPTION_TEXT[key]?.category;
			if (category) {
				if (!acc[category]) {
					acc[category] = [];
				}
				acc[category].push(key);
			}
			return acc;
		},
		{} as Record<ProctoringOptionCategory, RenderableProctoringKey[]>,
	);

	const getStrictModeEnabled = (optionKey: keyof ProctoringOption) =>
		verityInstituteSettings &&
		optionRelatedInstituteSettings &&
		getProctoringOptionDisabledDetails(
			optionKey,
			"strict",
			option,
			verityInstituteSettings,
			optionRelatedInstituteSettings,
		)?.allowedValues.includes("strict");

	const optionChildren = activeOptions.reduce((acc, key) => {
		const value = option[key];

		if (isTabKey(key)) {
			// No longer showing excludedWebTrafficRecordingUrls. It has been merged with tabWhiteList
			if (!acc.has("tabControl")) {
				acc.set("tabControl", []);
			}
			const group = acc.get("tabControl")!;

			if (
				key === "tabWhiteList" &&
				Array.isArray(value) &&
				value.length > 0 &&
				value.every((item) => typeof item === "string")
			) {
				group.push({
					text: PROCTORING_OPTION_TEXT["tabWhiteList"].header,
					children: value.map((url) => ({ text: url })),
					isList: true,
				});
			} else if (typeof value === "boolean" && value) {
				if (key === "closeOpenTabs" || key === "disableNewTab" || key === "recordWebTraffic") {
					group.push({
						text: (
							<Trans
								i18n={i18nPackage}
								i18nKey={`editQuizPage.configurationStep.otherActivitiesTSX.tabs.${key}`}
								components={{ b: <strong /> }}
							/>
						),
					});
				}
			}
		} else if (value && typeof value === "string") {
			acc.set(key, []);
			if (key === "applicationBlockMode") {
				let listTitle = "";
				if (activeOptions.includes("blockWindowedApplications")) {
					listTitle = activeOptions.includes("hideOtherWindows")
						? i18nPackage.t("editQuizPage.configurationStep.disableFeaturesTSX.blockApplications.modes.all")
						: i18nPackage.t("editQuizPage.configurationStep.disableFeaturesTSX.blockApplications.modes.allow-list");
				} else {
					listTitle = i18nPackage.t(
						"editQuizPage.configurationStep.disableFeaturesTSX.blockApplications.modes.block-list",
					);
				}

				const appChildren: NestedOption[] = [];

				// Only fetch list items if hideOtherWindows is FALSE
				if (!activeOptions.includes("hideOtherWindows")) {
					const listSource = activeOptions.includes("blockWindowedApplications")
						? option["allowedApplicationsList"]
						: option["applicationBlockList"];

					if (Array.isArray(listSource)) {
						listSource.forEach((appName) => appChildren.push({ text: appName }));
					}
				}

				acc.set(key, [
					{
						text: listTitle,
						children: appChildren,
						isList: true,
					},
				]);
			}

			if (key === "extensionBlockMode") {
				const listTitle = activeOptions.includes("allowedExtensionsList")
					? i18nPackage.t("editQuizPage.configurationStep.disableFeaturesTSX.blockExtensions.allow-list")
					: i18nPackage.t("editQuizPage.configurationStep.disableFeaturesTSX.blockExtensions.all");

				const extensionChildren: NestedOption[] = [];

				if (activeOptions.includes("allowedExtensionsList")) {
					const listSource = option["allowedExtensionsList"];
					if (Array.isArray(listSource)) {
						listSource.forEach((extName) => extensionChildren.push({ text: extName }));
					}
				}
				acc.set(key, [
					{
						text: listTitle,
						children: extensionChildren,
						isList: true,
					},
				]);
			}

			if (key === "disableClipboardMode") {
				if (value === "system") {
					acc.get(key)!.push({
						text: i18nPackage.t("editQuizPage.configurationSummaryListTSX.systemClipboard"),
					});
				}
			} else if (key === "enableCalculator") {
				const calc = option["enableCalculator"];

				const type =
					calc === "scientific"
						? i18nPackage.t("editQuizPage.configurationStep.showToolsTSX.calculator.scientific")
						: i18nPackage.t("editQuizPage.configurationStep.showToolsTSX.calculator.standard");
				acc.get(key)!.push({
					text: i18nPackage.t("editQuizPage.configurationSummaryListTSX.calculator", {
						type: type,
					}),
				});
			} else if (value !== "strict") {
				acc.get(key)!.push({ text: i18nPackage.t("editQuizPage.configurationSummaryListTSX.bypassMode") });
			}
			if (key === "videoRecordingMode") {
				if (activeOptions.includes("faceTrackingMode")) {
					if (option["faceTrackingMode"]) {
						acc.get(key)!.push({
							text: i18nPackage.t("editQuizPage.configurationSummaryListTSX.multiplePeople"),
							children:
								option["faceTrackingMode"] === "normal" && getStrictModeEnabled("faceTrackingMode")
									? [{ text: i18nPackage.t("editQuizPage.configurationSummaryListTSX.bypassMode") }]
									: undefined,
						});
					}
				}
				if (activeOptions.includes("objectDetectionMode")) {
					const objectDetectionValue = option["objectDetectionMode"];
					if (objectDetectionValue)
						acc.get(key)!.push({
							text: i18nPackage.t("editQuizPage.configurationSummaryListTSX.suspiciousObjects"),
						});
				}
				if (activeOptions.includes("secondaryVideoRecordingMode")) {
					const secondaryVideoValue = option["secondaryVideoRecordingMode"];
					if (secondaryVideoValue)
						acc.get(key)!.push({
							text: i18nPackage.t("editQuizPage.configurationSummaryListTSX.secondaryCamera"),
						});
				}
			} else if (key === "audioRecordingMode") {
				if (activeOptions.includes("quietEnvironment")) {
					const quietEnvironmentValue = option["quietEnvironment"];
					if (quietEnvironmentValue)
						acc.get(key)!.push({ text: i18nPackage.t("editQuizPage.configurationSummaryListTSX.quietEnv") });
				}
				if (activeOptions.includes("audioTranscription")) {
					const audioTranscriptionValue = option["audioTranscription"];
					if (audioTranscriptionValue)
						acc.get(key)!.push({
							text: i18nPackage.t("editQuizPage.configurationSummaryListTSX.audioTranscription"),
						});

					if (activeOptions.includes("flaggedKeywords")) {
						const totalAssessmentFlaggedKeywords = option["flaggedKeywords"]?.length ?? 0;
						const totalInstituteFlaggedKeywords = optionRelatedInstituteSettings?.flaggedKeywords?.length ?? 0;
						const totalFlaggedKeywords = totalAssessmentFlaggedKeywords + totalInstituteFlaggedKeywords;
						if (totalFlaggedKeywords)
							acc.get(key)!.push({
								text: `${i18nPackage.t("editQuizPage.configurationSummaryListTSX.flaggedKeywords")} (${totalFlaggedKeywords})`,
							});
					}
				}
			}
		} else if (value && typeof value === "boolean") {
			if (key === "idVerification") {
				acc.set(key, []);
				if (activeOptions.includes("idVerificationEnableAi")) {
					if (option["idVerificationEnableAi"]) {
						acc.get(key)!.push({
							text: i18nPackage.t("editQuizPage.configurationSummaryListTSX.aiIDVer"),
							children:
								activeOptions.includes("idVerificationMode") &&
								option["idVerificationMode"] === "normal" &&
								getStrictModeEnabled("idVerificationMode")
									? [{ text: i18nPackage.t("editQuizPage.configurationSummaryListTSX.bypassMode") }]
									: undefined,
						});
					}
				}
			}
			if (key === "enableGeofence") {
				acc.set(key, []);
				if (activeOptions.includes("geofenceLocations")) {
					const geofences = option["geofenceLocations"] ?? [];

					if (geofences.length > 0) {
						acc.get(key)!.push({
							text: i18nPackage.t("editQuizPage.configurationSummaryListTSX.selectedGeofence", {
								name: geofences[0].name,
							}),
						});
					}
				}
			}
			if (key === "recordInput") {
				if (activeOptions.includes("keyboardShortcutBlockList")) {
					const shortcuts = option["keyboardShortcutBlockList"] || [];
					if (shortcuts && shortcuts.length > 0) {
						acc.set(key, [
							{
								text: i18nPackage.t("editQuizPage.configurationStep.otherActivitiesTSX.keyboardList"),
								children: shortcuts.map((s) => ({ text: s })),
								isList: true,
							},
						]);
					}
				}
			}
		} else if (Array.isArray(value) && value.every((item) => typeof item === "string")) {
			acc.set(
				key,
				value.map((entry) => ({ text: entry })),
			);
		} else if (Array.isArray(value)) {
			if (key === "flaggedKeywords") {
				if (activeOptions.includes("flaggedKeywords")) {
					acc.set(key, [{ text: i18nPackage.t("editQuizPage.configurationSummaryListTSX.aiIDVer") }]);
				}
			}
		}
		return acc;
	}, new Map<RenderableProctoringKey, NestedOption[]>());

	return { optionCategories, optionChildren };
}
